import React, { useState, useEffect } from "react";
import { Select, Card, Descriptions, Space, Table } from "antd";
import { useList } from "@refinedev/core";
import { PURCHASE_REQUEST_LIST_APPROVED_QUERY } from "../../purchase-request/queries";
import { PurchaseRequest } from "../../purchase-request/interface"; // Assuming you have this interface
import dayjs from "dayjs";

interface PurchaseRequestSelectorProps {
  onSelect: (pr: PurchaseRequest | null) => void;
  initialPurchaseRequestId?: string;
}

export const PurchaseRequestSelector: React.FC<PurchaseRequestSelectorProps> = ({ onSelect, initialPurchaseRequestId }) => {
  const [selectedPrId, setSelectedPrId] = useState<string | undefined>(initialPurchaseRequestId);
  const [selectedPr, setSelectedPr] = useState<PurchaseRequest | null>(null);

  const { data, isLoading } = useList<PurchaseRequest>({
    resource: "purchaseRequests",
    meta: {
      gqlQuery: PURCHASE_REQUEST_LIST_APPROVED_QUERY,
    },
    filters: [
      {
        field: "status",
        operator: "eq",
        value: "APPROVED",
      },
    ],
  });

  const purchaseRequests = data?.data || [];

  useEffect(() => {
    if (initialPurchaseRequestId && purchaseRequests.length > 0) {
      const initialPr = purchaseRequests.find(pr => pr.id === initialPurchaseRequestId);
      if (initialPr) {
        setSelectedPr(initialPr);
        onSelect(initialPr);
      }
    }
  }, [initialPurchaseRequestId, purchaseRequests]);

  const handleSelectChange = (value: string) => {
    setSelectedPrId(value);
    const pr = purchaseRequests.find((p) => p.id === value) || null;
    setSelectedPr(pr);
    onSelect(pr);
  };

  const itemColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Qty", dataIndex: "quantity" },
    { title: "Unit Price", dataIndex: "unitPrice", render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}` },
    { title: "Total", dataIndex: "totalPrice", render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}` },
  ];

  return (
    <Card title="Link from Purchase Request" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          showSearch
          placeholder="Select an approved Purchase Request"
          optionFilterProp="children"
          onChange={handleSelectChange}
          value={selectedPrId}
          loading={isLoading}
          filterOption={(input, option) =>
            (option?.children as React.ReactNode)?.toString().toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: "100%" }}
        >
          {purchaseRequests.map((pr) => (
            <Select.Option key={pr.id} value={pr.id}>
              {pr.requestNo} - {pr.notes?.substring(0, 50)}... (Total: ETB {pr.totalAmount.toLocaleString()})
            </Select.Option>
          ))}
        </Select>

        {selectedPr && (
          <Descriptions bordered column={2} size="small" style={{ marginTop: 16 }}>
            <Descriptions.Item label="Request No">{selectedPr.requestNo}</Descriptions.Item>
            <Descriptions.Item label="Team">{selectedPr.teamId}</Descriptions.Item>
            <Descriptions.Item label="Date Requested">{dayjs(selectedPr.dateRequested).format("YYYY-MM-DD")}</Descriptions.Item>
            <Descriptions.Item label="Date Required">{dayjs(selectedPr.dateRequired).format("YYYY-MM-DD")}</Descriptions.Item>
            <Descriptions.Item label="Budget Source">{selectedPr.budgetSource}</Descriptions.Item>
            <Descriptions.Item label="Budget ID">{selectedPr.budgetId || "-"}</Descriptions.Item>
            <Descriptions.Item label="Total Amount">ETB {selectedPr.totalAmount.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Currency">{selectedPr.currency}</Descriptions.Item>
            <Descriptions.Item label="Payment Terms">{selectedPr.paymentTerms}</Descriptions.Item>
            <Descriptions.Item label="Shipping Method">{selectedPr.shippingInfo?.method}</Descriptions.Item>
            <Descriptions.Item label="Vendor">{selectedPr.vendor?.companyName || "-"}</Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>{selectedPr.notes || "-"}</Descriptions.Item>
            <Descriptions.Item label="Terms and Conditions" span={2}>{selectedPr.termsAndConditions || "-"}</Descriptions.Item>
          </Descriptions>
        )}

        {selectedPr && selectedPr.items && selectedPr.items.length > 0 && (
          <Card title="Items from Purchase Request" size="small" style={{ marginTop: 16 }}>
            <Table dataSource={selectedPr.items} columns={itemColumns} rowKey="id" pagination={false} />
          </Card>
        )}
      </Space>
    </Card>
  );
};